import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { HeaderNav } from "@/components/HeaderNav";
import { GlimpseAuth } from "@/components/glimpse/GlimpseAuth";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const Intro = () => {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/glimpse/state").then((r) => {
      if (r.data.glimpse?.status === "completed") navigate("/glimpse/portrait", { replace: true });
      else setState(r.data);
    }).catch(() => setState({}));
  }, [navigate]);
  const begin = async () => {
    setBusy(true);
    try { await api.post("/glimpse/start"); navigate("/glimpse/questions"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  if (!state) return <div className="max-w-2xl mx-auto px-5 pt-24 cg-eyebrow animate-pulse">Loading…</div>;
  const resuming = state.glimpse?.status === "in_progress" && state.glimpse.answered > 0;
  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 pt-14 pb-24 cg-fade-up" data-testid="glimpse-intro">
      <p className="cg-eyebrow mb-3">Free · introductory reflection</p>
      <h1 className="font-serif text-4xl sm:text-5xl text-[#F3F4F6] mb-6">Your Authority Glimpse</h1>
      <div className="cg-card p-8 space-y-4 text-[#B9BDC7] leading-relaxed">
        <p className="font-serif text-2xl text-[#F3F4F6]">Where does your authority lie?</p>
        <p>Our decisions are shaped by many influences, including expectations, uncertainty, relationships, personal practices and our connection to ourselves.</p>
        <p>This short experience introduces you to seven dimensions of personal authority.</p>
        <p>You'll answer 21 statements and receive a personalized Glimpse Portrait.</p>
        <p data-testid="glimpse-instruction-area">Think about an area of your life where, over the past 6 to 12 months, you have found it difficult to make decisions or trust your own judgement. This could be at work, with family, in your relationships, or in your community. Keep this area in mind as you answer the questions.</p>
        <p>If your experience changes depending on the situation, choose the answer that best describes how you usually feel or respond in the area of life you identified above.</p>
        <p className="text-[#E8EAEE]">Remember, your answers reflect your experiences in this area of life. They do not define who you are in every situation.</p>
        {state.typical_minutes && <p className="text-sm text-[#9CA3AF]" data-testid="glimpse-duration">Most people finish in about {state.typical_minutes} minute{state.typical_minutes === 1 ? "" : "s"}.</p>}
        <button data-testid="glimpse-begin-button" onClick={begin} disabled={busy}
          className="mt-2 inline-flex items-center gap-2 bg-brass text-obsidian font-medium px-7 py-3.5 rounded-full cg-scale-btn hover:bg-amber2 disabled:opacity-60">
          {resuming ? `Continue My Glimpse (${state.glimpse.answered} of 21)` : "Begin My Glimpse"} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default function Glimpse() {
  const { user, ready } = useAuth();
  let body;
  if (!ready || user === null) body = <div className="max-w-2xl mx-auto px-5 pt-24 cg-eyebrow animate-pulse">Loading…</div>;
  else if (!user) body = <GlimpseAuth />;
  else if (user.role !== "participant") body = <div className="max-w-xl mx-auto px-5 pt-24 text-[#9CA3AF]" data-testid="glimpse-not-participant">The Authority Glimpse is available to participant accounts. Please sign in with a personal account.</div>;
  else body = <Intro />;
  return <div><HeaderNav />{body}</div>;
}
